import { type PatchBuildingByIdRequestBody } from "@eisox/api/core-api";
import { Button } from "@eisox/design-system";
import { useAppForm } from "@eisox/form";
import { ArrowRightIcon } from "@eisox/icons";
import { getDirtyValues } from "@eisox/tools";
import { useSelector } from "@tanstack/react-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";

import { buildingApi } from "~/api";
import { TEMPERATURE_MAX, TEMPERATURE_MIN, TEMPERATURE_STEP } from "~/constants/temperature.constant";
import { clampTemperature, getHouseTemperatureBounds } from "~/helpers/temperature.helper";
import { m } from "~/paraglide/messages";
import { useBuildingContext } from "~/providers/building.provider";

export const Route = createFileRoute("/_authenticated/buildings_/$buildingId/settings/buildings/temperatures")({
  component: RouteComponent,
});

function RouteComponent() {
  const { building, rooms, permissions } = useBuildingContext(Route.id);

  const queryClient = useQueryClient();

  const { mutateAsync: update } = useMutation({
    mutationFn: (body: PatchBuildingByIdRequestBody) => buildingApi.update(building.id, body),
    onSuccess: async () => {
      await queryClient.invalidateQueries(buildingApi.queries.byId(building.id));
    },
  });

  const form = useAppForm({
    defaultValues: {
      absenceTemperature: building.absenceTemperature,
      preComfortTemperature: building.preComfortTemperature,
    },
    onSubmit: async ({ formApi, value }) => {
      const dirty = getDirtyValues(formApi.options.defaultValues, value);
      if (!dirty) return;
      await update(dirty);
      formApi.reset();
    },
  });

  const absenceTemperature = useSelector(form.store, state => state.values.absenceTemperature);
  const preComfortTemperature = useSelector(form.store, state => state.values.preComfortTemperature);
  const { absenceTemperatureBounds, preComfortTemperatureBounds } = getHouseTemperatureBounds({
    frostFreeTemperature: building.frostFreeTemperature,
    preComfortTemperature,
    absenceTemperature,
    rooms,
  });

  return (
    <form
      data-testid="HouseTemperaturesSettings"
      className="gap-2.5 flex h-full flex-col overflow-x-hidden overflow-y-auto"
      onSubmit={e => {
        e.preventDefault();
        void form.handleSubmit();
      }}
    >
      <form.AppForm>
        <form.AppField name="absenceTemperature">
          {field => (
            <field.Slider
              className="max-w-105"
              label={`${m.absence_temperature()} (°C)`}
              min={TEMPERATURE_MIN}
              max={TEMPERATURE_MAX}
              step={TEMPERATURE_STEP}
              valueLabelDisplay="auto"
              valueLabelFormat={`${absenceTemperatureBounds.min - TEMPERATURE_STEP}°C (${m.frost_free()}) < ${field.state.value}°C (${m.absence()}) < ${preComfortTemperatureBounds.max + TEMPERATURE_STEP}°C (${m.pre_comfort()})`}
              onValueChange={nextValue => {
                if (nextValue === undefined) return;
                field.handleChange(clampTemperature(nextValue, absenceTemperatureBounds));
              }}
              disabled={!permissions.building?.absenceTemperature?.update}
            />
          )}
        </form.AppField>
        <form.AppField name="preComfortTemperature">
          {field => (
            <field.Slider
              className="max-w-105"
              label={`${m.pre_comfort_temperature()} (°C)`}
              min={TEMPERATURE_MIN}
              max={TEMPERATURE_MAX}
              step={TEMPERATURE_STEP}
              valueLabelDisplay="auto"
              valueLabelFormat={`${preComfortTemperatureBounds.min - TEMPERATURE_STEP}°C (${m.absence()}) < ${field.state.value}°C (${m.pre_comfort()}) < ${preComfortTemperatureBounds.max + TEMPERATURE_STEP}°C (${m.comfort()})`}
              onValueChange={nextValue => {
                if (nextValue === undefined) return;
                field.handleChange(clampTemperature(nextValue, preComfortTemperatureBounds));
              }}
              disabled={!permissions.building?.preComfortTemperature?.update}
            />
          )}
        </form.AppField>
        <Button asChild>
          <Link to={"/buildings/$buildingId/settings/buildings/holidays"} params={{ buildingId: building.id }}>
            {m.frost_free_temperature()} <ArrowRightIcon />
          </Link>
        </Button>
        {rooms.length > 0 && (
          <Button asChild>
            <Link to={"/buildings/$buildingId/settings/rooms/type-temp"} params={{ buildingId: building.id }}>
              {m.comfort_and_night_temperature_per_room()} <ArrowRightIcon />
            </Link>
          </Button>
        )}
        <form.SubmitButton className="mb-0 mx-auto mt-auto" label={m.save()} />
      </form.AppForm>
    </form>
  );
}
